const UNDERSCORE_OR_SLASH_RUN_PATTERN = /[_/]+/g
const WHITESPACE_RUN_PATTERN = /\s+/g

// Pure helpers that put moderation findings into words for the composer,
// the moderation queue and the Command Center preflight. Nothing here
// fetches or renders.
//
// A phrase finding (an org rule or the platform profanity list) carries the
// field it matched in and a code-point offset and length into that field; it
// never carries the matched words. When the caller passes the text the
// finding was computed against (the editor's own submission, or the review
// snapshot), the words are sliced out of it; otherwise the finding names only
// the field. Rule ids and rule phrases are never shown.

const PHRASE_CATEGORIES = new Set(['restricted_phrase', 'profanity'])

const FIELD_LABELS = {
  text: 'Text',
  title: 'Title',
  subtitle: 'Subtitle',
  cta_label: 'Call to action',
  'presentation.eyebrow': 'Eyebrow',
  'presentation.headline': 'Headline',
  'presentation.rationale': 'Rationale',
  'presentation.badge': 'Badge',
  // Public labels checked against the phrase rules when they are saved.
  tag_label: 'Tag',
  creator_name: 'Creator name',
  cta_target: 'Link',
  caption_label: 'Caption label',
}

const TRANSCRIPT_PREFIX = 'transcript:'

// Plain words for the provider's categories: OpenAI moderation's names, which
// the fake provider reuses. categoryWords spells out any other category.
const CATEGORY_WORDS = {
  sexual: 'sexual content',
  'sexual/minors': 'sexual content involving minors',
  harassment: 'harassment',
  'harassment/threatening': 'threatening harassment',
  hate: 'hate',
  'hate/threatening': 'threatening hate',
  illicit: 'illicit activity',
  'illicit/violent': 'violent illicit activity',
  'self-harm': 'self-harm',
  'self-harm/intent': 'intent to self-harm',
  'self-harm/instructions': 'self-harm instructions',
  violence: 'violence',
  'violence/graphic': 'graphic violence',
  unspecified: 'unspecified content',
}

// Where a provider finding was raised, as the end of "Flagged for hate in …".
const MODALITY_WORDS = {
  text: 'the text',
  image: 'an image',
  video: 'the video',
  audio: 'the soundtrack',
}

// categoryWords names a provider category in plain words ("sexual/minors"
// reads "sexual content involving minors"). An unknown category is spelled
// out from its code, its underscores and slashes read as spaces.
export function categoryWords(category) {
  if (!category) return CATEGORY_WORDS.unspecified
  if (CATEGORY_WORDS[category]) return CATEGORY_WORDS[category]
  return String(category).replace(UNDERSCORE_OR_SLASH_RUN_PATTERN, ' ').replace(WHITESPACE_RUN_PATTERN, ' ').trim() || CATEGORY_WORDS.unspecified
}

export function isPhraseFinding(finding) {
  return Boolean(finding) && PHRASE_CATEGORIES.has(finding.category)
}

// isTranscriptField reports whether a finding field points into speech.
export function isTranscriptField(field) {
  return typeof field === 'string' && field.startsWith(TRANSCRIPT_PREFIX)
}

// isSpokenFinding reports whether a finding was raised on the speech of a
// video rather than on typed text or pixels.
export function isSpokenFinding(finding) {
  return Boolean(finding) && (finding.applies_to === 'audio' || isTranscriptField(finding.field))
}

// transcriptFieldKey is the texts key of one transcript chunk, the same
// string a phrase finding matched in that chunk carries as its field.
export function transcriptFieldKey(assetId, offsetMs) {
  return `${TRANSCRIPT_PREFIX}${assetId}:${offsetMs}`
}

// transcriptFieldOffset returns the chunk offset in milliseconds a field
// transcript:<asset id>:<offset ms> names (phrase and classifier findings
// alike; the worker relabels the adapter's transcript:<chunk index>), or null
// for any other field.
function transcriptFieldOffset(field) {
  if (!isTranscriptField(field)) return null
  const parts = field.slice(TRANSCRIPT_PREFIX.length).split(':')
  if (parts.length < 2) return null
  const offset = Number(parts[parts.length - 1])
  return Number.isInteger(offset) && offset >= 0 ? offset : null
}

// formatTimeOffset renders a media offset in milliseconds as m:ss, or h:mm:ss
// from an hour on ("0:32", "12:05", "1:02:03"). It is '' for a missing or
// negative offset.
export function formatTimeOffset(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return ''
  const total = Math.floor(ms / 1000)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = String(total % 60).padStart(2, '0')
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`
}

// findingTimeOffset is the media time a finding points at, in milliseconds:
// its time_offset_ms, or the chunk offset its transcript field names.
export function findingTimeOffset(finding) {
  if (typeof finding?.time_offset_ms === 'number' && Number.isFinite(finding.time_offset_ms) && finding.time_offset_ms >= 0) {
    return finding.time_offset_ms
  }
  return transcriptFieldOffset(finding?.field)
}

function atTime(ms) {
  const time = formatTimeOffset(ms)
  return time ? ` at ${time}` : ''
}

// findingFieldLabel names a phrase finding's field the way the composer and
// the review snapshot label it. Wager labels are matched in sorted order, so
// their index is not shown.
export function findingFieldLabel(field) {
  if (!field) return 'Text'
  if (FIELD_LABELS[field]) return FIELD_LABELS[field]
  if (isTranscriptField(field)) return `Speech${atTime(transcriptFieldOffset(field))}`
  if (field.startsWith('label:')) return 'Wager label'
  return field.replaceAll('_', ' ').replaceAll('.', ' ')
}

// findingRange clamps a finding's span to the text, in code points. It is
// null when the finding has no usable offsets or the span falls outside the
// text (for example after the field changed).
function findingRange(finding, length) {
  if (!Number.isInteger(finding?.offset) || !Number.isInteger(finding?.length) || finding.length <= 0) return null
  const start = finding.offset
  const end = finding.offset + finding.length
  if (start < 0 || end > length) return null
  return { start, end }
}

// matchedWords returns the words a phrase finding points at in text, or ''
// when they cannot be located.
export function matchedWords(finding, text) {
  if (typeof text !== 'string' || !text) return ''
  const codePoints = Array.from(text)
  const range = findingRange(finding, codePoints.length)
  if (!range) return ''
  return codePoints.slice(range.start, range.end).join('').trim()
}

// findingLabel describes one finding in plain words. texts maps a finding
// field (text, title, subtitle, cta_label, presentation.*) to the text the
// finding was computed against; pass it only when that text is the exact
// submitted or snapshotted value. A transcript chunk's text is keyed by the
// chunk's field (see snapshotTexts).
export function findingLabel(finding, texts = {}) {
  if (!finding) return ''
  const spoken = isSpokenFinding(finding)
  const at = atTime(findingTimeOffset(finding))
  if (isPhraseFinding(finding)) {
    const field = finding.field || 'text'
    if (spoken) {
      // Phrases in speech are only ever flagged: a transcript is too
      // uncertain to refuse a save on.
      const spokenWords = matchedWords(finding, texts?.[field])
      const spokenSubject = spokenWords
        ? `Spoken “${spokenWords}”`
        : `A spoken ${finding.category === 'profanity' ? 'word' : 'phrase'}`
      return `${spokenSubject}${at} needs brand review`
    }
    const where = findingFieldLabel(field)
    const words = matchedWords(finding, texts?.[field])
    const subject = words ? `“${words}” in ${where}` : `A ${finding.category === 'profanity' ? 'word' : 'phrase'} in ${where}`
    return finding.severity === 'block'
      ? `${subject} is not allowed for this brand`
      : `${subject} needs brand review`
  }
  // A provider finding reads as a sentence: "Flagged for violence in the
  // video at 0:32", "Blocked: sexual content involving minors in the text".
  const words = categoryWords(finding.category)
  const where = spoken ? MODALITY_WORDS.audio : (MODALITY_WORDS[finding.applies_to] || 'the post')
  return finding.severity === 'block'
    ? `Blocked: ${words} in ${where}${at}`
    : `Flagged for ${words} in ${where}${at}`
}

// PLATFORM_FINDING_SOURCE is the source the API gives a platform phrase
// finding below internal_org_admin: Wager's word list is internal, and a
// platform rule's id names its word.
const PLATFORM_FINDING_SOURCE = 'platform'

// findingSourceLabel names what raised a finding in a review's technical
// details: the rule id or model as recorded, or "Wager rule" for a platform
// rule the reader may not see.
export function findingSourceLabel(source) {
  return source === PLATFORM_FINDING_SOURCE ? 'Wager rule' : source || ''
}

// uniqueFindingLabels labels findings once each, in order: two rules that
// match the same words read as one line.
export function uniqueFindingLabels(findings = [], texts = {}) {
  return [...new Set((findings || []).map((finding) => findingLabel(finding, texts)).filter(Boolean))]
}

export function summarizeFindings(findings = [], texts = {}) {
  return uniqueFindingLabels(findings, texts).join('; ')
}

// highlightSegments splits text around the phrase findings for one field.
// Offsets and lengths are in Unicode code points, as the server counts them.
// Overlapping or touching matches merge into one highlighted run.
export function highlightSegments(text = '', findings = [], field = 'text') {
  const codePoints = Array.from(text || '')
  const ranges = (findings || [])
    .filter((finding) => (finding?.field || 'text') === field)
    .map((finding) => {
      const range = findingRange(finding, codePoints.length)
      return range ? { ...range, severity: finding.severity } : null
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start)
  const merged = []
  ranges.forEach((range) => {
    const last = merged[merged.length - 1]
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end)
      if (range.severity === 'block') last.severity = 'block'
      return
    }
    merged.push({ ...range })
  })
  const segments = []
  let cursor = 0
  merged.forEach((range) => {
    if (range.start > cursor) segments.push({ text: codePoints.slice(cursor, range.start).join(''), highlighted: false })
    segments.push({ text: codePoints.slice(range.start, range.end).join(''), highlighted: true, severity: range.severity })
    cursor = range.end
  })
  if (cursor < codePoints.length) segments.push({ text: codePoints.slice(cursor).join(''), highlighted: false })
  return segments
}

// snapshotTexts maps a review snapshot onto the finding fields its phrase
// findings count into. Wager labels are left out: the matcher sees them
// sorted, so their index does not identify a snapshot label. transcripts,
// the review detail's [{asset_id, chunks: [{offset_ms, text}]}], adds one
// entry per chunk keyed transcript:<asset id>:<offset ms>, the field a
// phrase matched in that chunk carries.
export function snapshotTexts(snapshot, transcripts) {
  const spoken = {}
  for (const transcript of Array.isArray(transcripts) ? transcripts : []) {
    if (!transcript?.asset_id || !Array.isArray(transcript.chunks)) continue
    for (const chunk of transcript.chunks) {
      if (!Number.isInteger(chunk?.offset_ms) || typeof chunk.text !== 'string') continue
      spoken[transcriptFieldKey(transcript.asset_id, chunk.offset_ms)] = chunk.text
    }
  }
  if (!snapshot) return spoken
  return {
    ...spoken,
    text: snapshot.text,
    title: snapshot.title,
    subtitle: snapshot.subtitle,
    cta_label: snapshot.cta_label,
    'presentation.eyebrow': snapshot.presentation?.eyebrow,
    'presentation.headline': snapshot.presentation?.headline,
    'presentation.rationale': snapshot.presentation?.rationale,
    'presentation.badge': snapshot.presentation?.badge,
  }
}
